import { Button } from "components/DesignSystem/Button/Button";
import { Cross } from "components/icons/Cross";
import { Fire } from "components/icons/Fire";
import { SparkleGrey } from "components/icons/SparkleGrey";
import { PaperPlaneSkyblue } from "components/icons/PaperPlaneSkyblue";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "components/DesignSystem/ToggleGroup/ToggleGroup";
import { Divider } from "components/design/Divider";
import { Subscription } from "types/Models/subscription";
import { GreentickGreenCircle } from "components/icons/GreentickGreenCircle";
import Radio from "components/DesignSystem/RadioGroup/RadioGroup";
import classNames from "classnames";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { PlanCardButton } from "./PlanCardButton";

export type PlanCardProps = {
  description?: string;
  price?: number | string;
  billingCycle?: string;
  isButtonDisabled?: boolean;
  isActive?: boolean;
  type?: string;
  buttonText?: string;
  features?: { description: string; active: boolean }[];
  icon?: React.ReactNode;
  isMostPopular?: boolean;
  planOptions?: Subscription[];
  onPlanOptionSelect?: (option: Subscription) => void;
  onUpgrade?: () => void;
  includeStartsFrom?: boolean;
  buttonType?: "primary_outlined" | "secondary";
  hideUpgrade?: boolean;
  containsRadio?: boolean;
  onRadioChange?: (value: string) => void;
  isRadioDisabled?: boolean;
  radioValue?: string;
  toggleValue?: string;
  isAnyPlanActive?: boolean;
  onDowngrade?: () => void;
  isLoading?: boolean;
  isAddToCartFlow?: boolean;
  handleAddToCart?: () => void;
  children?: React.ReactNode;
};

const MostPopularBadge = () => (
  <div className="t-text-body-sm t-text-text-30 t-bg-surface-lighter-grey t-border t-border-solid t-border-neutral-0 t-w-fit t-px-2 t-py-1 t-absolute t-top-0 t-right-0 t-rounded-bl-lg t-flex t-items-center t-justify-center t-gap-1.5">
    <div className="t-flex t-items-center t-justify-center t-w-3 t-h-3">
      <Fire />
    </div>
    MOST POPULAR
  </div>
);

export const PlanCard = ({
  description,
  price = 0,
  billingCycle,
  isButtonDisabled,
  isActive,
  type,
  buttonText = "Subscribe",
  icon = <PaperPlaneSkyblue />,
  isMostPopular,
  features,
  planOptions,
  onPlanOptionSelect,
  onUpgrade,
  includeStartsFrom,
  buttonType = "primary_outlined",
  hideUpgrade,
  containsRadio,
  onRadioChange,
  isRadioDisabled,
  radioValue,
  toggleValue,
  isAnyPlanActive = true,
  onDowngrade,
  isLoading,
  isAddToCartFlow,
  handleAddToCart,
  children,
}: PlanCardProps) => {
  const monthlyOption = planOptions?.find((o) => o.billing_cycle === "MONTHLY");
  const yearlyOption = planOptions?.find((o) => o.billing_cycle === "YEARLY");
  const savingsPercent =
    monthlyOption && yearlyOption
      ? Math.round(
          ((monthlyOption.amount * 12 - yearlyOption.amount) /
            (monthlyOption.amount * 12)) *
            100
        )
      : null;

  return (
    <div
      className={classNames("t-w-80 t-h-auto t-px-1 t-py-1 t-rounded-lg", {
        "t-bg-green-10": isActive,
      })}
    >
      <div
        className={classNames(
          "t-w-full t-text-center t-text-caption t-text-green-80 t-py-1",
          {
            "t-h-0": !isAnyPlanActive,
            "t-h-6": isAnyPlanActive,
          }
        )}
      >
        {isActive ? "ACTIVE PLAN" : ""}
      </div>
      <section
        onClick={!isRadioDisabled ? () => onRadioChange?.(type!) : undefined}
        className={classNames(
          `t-w-full t-border t-h-[calc(100%-24px)] t-border-solid  t-rounded-lg t-flex t-flex-col t-gap-4 t-px-5 t-py-6 t-relative t-overflow-hidden t-bg-surface`,
          {
            "t-cursor-not-allowed": containsRadio && isRadioDisabled,
            "t-border-neutral-10": !isActive,
            "t-border-neutral-0": isActive,
          }
        )}
      >
        {isMostPopular && <MostPopularBadge />}
        <div className="t-flex t-gap-2 t-justify-between t-w-full t-h-10 t-rounded-full">
          {icon}
          {containsRadio && (
            <div className="-t-mr-3">
              <Radio.Root
                disabled={isRadioDisabled}
                onClick={(e) => e.stopPropagation()}
                value={radioValue}
                onValueChange={onRadioChange}
              >
                <Radio.Content>
                  <Radio.Item
                    onClick={(e) => e.stopPropagation()}
                    value={type || ""}
                    disabled={isRadioDisabled}
                  ></Radio.Item>
                </Radio.Content>
              </Radio.Root>
            </div>
          )}
        </div>
        <div className="t-flex t-flex-col t-gap-1.5">
          <div className="t-text-subtitle-lg">{type}</div>
          <div className="t-text-body-sm t-text-i-surface-grey-300 t-h-9">
            {description}
          </div>
        </div>
        <section className="t-text-subtext t-flex t-items-center t-text-text-60">
          {includeStartsFrom && (
            <div className="t-text-body-sm t-pr-1.5 t-text-i-surface-grey-300">
              Starts from
            </div>
          )}
          <div>$</div>
          <div>{price}</div>
          {billingCycle && (
            <div className="t-text-subtext t-text-text-30">/{billingCycle}</div>
          )}
        </section>
        {planOptions && planOptions?.length > 0 && (
          <div
            className="t-border-neutral-0 t-bg-surface"
            onClick={(e) => e.stopPropagation()}
          >
            <ToggleGroup
              defaultValue={toggleValue || planOptions?.[0]?.uuid}
              onValueChange={(value) => {
                const selectedOption = planOptions?.find(
                  (option) => option.uuid === value
                );
                onPlanOptionSelect?.(selectedOption!);
              }}
            >
              {planOptions?.map((option) => (
                <ToggleGroupItem key={option.uuid} value={option.uuid}>
                  {option.billing_cycle === "MONTHLY" ? (
                    "Monthly"
                  ) : (
                    <span className="t-flex t-items-center t-gap-1.5">
                      Annually
                      {savingsPercent !== null && savingsPercent > 0 && (
                        <Tag tagType="purple" icon={false} size="small" rounded>
                          Save {savingsPercent}%
                        </Tag>
                      )}
                    </span>
                  )}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
        )}
        {children}
        <PlanCardButton
          type={type}
          isActive={isActive}
          buttonType={buttonType}
          onUpgrade={onUpgrade}
          isButtonDisabled={isButtonDisabled}
          buttonText={buttonText}
          hideUpgrade={hideUpgrade}
          onDowngrade={onDowngrade}
          isLoading={isLoading}
          isAddToCartFlow={isAddToCartFlow}
          handleAddToCart={handleAddToCart}
        />
        <Divider>
          <section className="t-flex t-gap-1 t-items-center">
            <SparkleGrey height={10} />
            <SparkleGrey />
            <SparkleGrey height={10} />
          </section>
        </Divider>
        {features && features.length > 0 && (
          <section className="t-flex t-flex-col t-gap-2">
            {features.map((feature) => (
              <div
                key={feature.description}
                className="t-flex t-gap-3 t-items-center t-text-text-30 t-text-body-sm"
              >
                {feature.active ? (
                  <div className="t-w-4 t-h-4 t-flex t-items-center">
                    <GreentickGreenCircle className="t-text-green-70" />
                  </div>
                ) : (
                  <div className="t-w-4 t-h-4 t-flex t-items-center">
                    <div className="t-w-3 t-h-3 t-border t-border-solid t-border-text-30 t-rounded-full t-flex t-items-center t-justify-center">
                      <div className="t-w-2 t-h-2 t-flex t-items-center t-justify-center">
                        <Cross />
                      </div>
                    </div>
                  </div>
                )}
                <div>{feature.description}</div>
              </div>
            ))}
          </section>
        )}
      </section>
    </div>
  );
};
